import React, { useState, useEffect } from 'react';
import GlobalSnackbar from './components/common/GlobalSnackbar';
import { apiError$ } from './api/client';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Menu, MenuItem, IconButton, Avatar, Box, Container } from '@mui/material';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import Person from '@mui/icons-material/Person';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Accounts from './pages/Accounts.tsx';
import Transactions from './pages/Transactions.tsx';
import Home from './pages/Home.tsx';
import RecentTransactions from './pages/RecentTransactions.tsx';
import Charges from './pages/Charges.tsx';
import ThirdPartyTransfer from './pages/ThirdPartyTransfer.tsx';

import SettingsPage from './pages/Settings';
import AboutUs from './pages/AboutUs';
import Help from './pages/Help';
import Share from './pages/Share';
import AppInfo from './pages/AppInfo';
import LogoutPage from './pages/Logout';
import Loans from './pages/Loans';
import Savings from './pages/Savings';
import Notifications from './pages/Notifications';
import QR from './pages/QR';
import Guarantor from './pages/Guarantor';
import ClientCharges from './pages/ClientCharges';
import UpdatePassword from './pages/UpdatePassword';
import Location from './pages/Location';
import Locations from './pages/Locations';
import Survey from './pages/Survey';
import StatementPdf from './pages/StatementPdf';
import ProtectedRoute from './components/ProtectedRoute';
import Profile from './pages/Profile';
import AccountVerification from './pages/AccountVerification';
import LeftSidebar from './components/LeftSidebar';
import logo from './logo.png';
import LoanDetails from './pages/LoanDetails';
import LoanApplications from './pages/LoanApplications';
import LoanUpdate from './pages/LoanUpdate';
import Payments from './pages/Payments';
import PaymentReturn from './pages/PaymentReturn';
import Statements from './pages/Statements.tsx';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AdminDashboard from './pages/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import { validateAndCleanTokens, isAuthenticated, clearAuthData, getToken, isAccessTokenValid } from './utils/auth';
import { refreshToken as backendRefreshToken } from './utils/refreshToken';
import { jwtDecode } from 'jwt-decode';
import { AuthProvider } from './context/AuthContext';

const clientPrimaryLinks = [
  { to: '/accounts', label: 'Loan Accounts' },
  { to: '/savings', label: 'Savings Accounts' },
  { to: '/payments', label: 'Payments' },
  { to: '/third-party-transfer', label: 'Third Party Transfer' },
];

// Base admin links (available to all admin roles)
const baseAdminPrimaryLinks = [
  { to: '/admin', label: 'Products', tabIndex: 1 },
  { to: '/admin', label: 'Payment Management', tabIndex: 2 },
  { to: '/admin', label: 'ZIG Rate Management', tabIndex: 3 },
  { to: '/admin', label: 'Client Management', tabIndex: 4 },
  { to: '/admin', label: 'Metrics & Analytics', tabIndex: 5 },
  { to: '/admin', label: 'Operations', tabIndex: 7 }, // Tab 7 when Audit Logs visible, 6 when hidden
];

// Helper function to get admin links based on role
const getAdminPrimaryLinks = (): Array<{ to: string; label: string; tabIndex: number }> => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) {
      // If no token, return base links with Payment Management at tab 6
      return [
        ...baseAdminPrimaryLinks,
        { to: '/admin', label: 'Payment Management', tabIndex: 6 },
      ];
    }
    
    const { jwtDecode } = require('jwt-decode');
    const decoded: any = jwtDecode(token);
    
    const realmRoles = decoded.realm_access?.roles || [];
    const resourceRoles: string[] = [];
    
    if (decoded.resource_access) {
      Object.values(decoded.resource_access).forEach((client: any) => {
        if (client.roles) {
          resourceRoles.push(...client.roles);
        }
      });
    }
    
    const allRoles = [...realmRoles, ...resourceRoles];
    const canViewAuditLogs = allRoles.some((role: string) => {
      const upperRole = role.toUpperCase();
      return upperRole === 'ADMINISTRATOR' || 
             upperRole === 'ROLE_ADMINISTRATOR' ||
             upperRole === 'AUDIT' ||
             upperRole === 'ROLE_AUDIT';
    });
    
    // If user is ADMINISTRATOR or AUDIT, include Audit Logs at tab 6
    if (canViewAuditLogs) {
      return [
        ...baseAdminPrimaryLinks.slice(0, 5), // Products, Payment Management, ZIG Rate, Client Management, Metrics
        { to: '/admin', label: 'Audit Logs', tabIndex: 6 },
        ...baseAdminPrimaryLinks.slice(5), // Operations
      ];
    }
    
    // For non-ADMINISTRATOR/AUDIT admin roles, Operations is at tab 6
    return baseAdminPrimaryLinks.map(link => {
      if (link.tabIndex === 7) {
        return { ...link, tabIndex: 6 }; // Operations moves to tab 6 when Audit Logs is hidden
      }
      return link;
    });
  } catch (error) {
    // On error, return base links with Payment Management at tab 6
    return [
      ...baseAdminPrimaryLinks,
      { to: '/admin', label: 'Payment Management', tabIndex: 6 },
    ];
  }
};

// Helper function to check if user is admin (any admin role)
const isAdmin = (): boolean => {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    const { jwtDecode } = require('jwt-decode');
    const decoded: any = jwtDecode(token);
    
    const realmRoles = decoded.realm_access?.roles || [];
    const resourceRoles: string[] = [];
    
    if (decoded.resource_access) {
      Object.values(decoded.resource_access).forEach((client: any) => {
        if (client.roles) {
          resourceRoles.push(...client.roles);
        }
      });
    }
    
    const allRoles = [...realmRoles, ...resourceRoles];
    
    // Define all admin roles that should have access to admin dashboard
    const adminRoles = [
      'ADMINISTRATOR', 'ROLE_ADMINISTRATOR',
      'ACCOUNTING_ASSISTANT', 'ROLE_ACCOUNTING_ASSISTANT',
      'ASSISTANT_ACCOUNTANT', 'ROLE_ASSISTANT_ACCOUNTANT',
      'CEO', 'ROLE_CEO',
      'FINANCE_MANAGER', 'ROLE_FINANCE_MANAGER',
      'OPERATIONS_MANAGER', 'ROLE_OPERATIONS_MANAGER',
      'OPERATIONS', 'ROLE_OPERATIONS',
      'FINANCE_DIRECTOR', 'ROLE_FINANCE_DIRECTOR',
      'OPERATIONS_DIRECTOR', 'ROLE_OPERATIONS_DIRECTOR',
      'RISK', 'ROLE_RISK',
      'AUDITOR', 'ROLE_AUDITOR',
      'AUDIT', 'ROLE_AUDIT'
    ];
    
    return allRoles.some((role: string) => 
      adminRoles.includes(role.toUpperCase())
    );
  } catch (error) {
    return false;
  }
};

const AppBarWithLinks: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<{ name: string; avatar: string }>({ name: '', avatar: '' });
  const [isAdminUser, setIsAdminUser] = useState(false);

  // Helper function to get initials from full name
  const getInitials = (fullName: string): string => {
    const names = fullName.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return fullName[0]?.toUpperCase() || 'U';
  };

  // Load user data from localStorage and check admin status
  useEffect(() => {
    const loadUserData = () => {
      try {
        const userInfo = localStorage.getItem('user_info');
        if (userInfo) {
          const userData = JSON.parse(userInfo);
          const firstName = userData.firstName || userData.given_name || userData.first_name || '';
          const lastName = userData.lastName || userData.family_name || userData.last_name || '';
          const fullName = firstName && lastName ? `${firstName} ${lastName}` : 
                          userData.username || userData.preferred_username || 'User';
          
          setUser({
            name: fullName,
            avatar: userData.avatar || ''
          });
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setUser({ name: 'User', avatar: '' });
      }
      
      // Check if user is admin
      setIsAdminUser(isAdmin());
    };

    loadUserData();
    
    // Listen for user info changes (e.g., after login or profile update)
    const handleUserInfoChange = () => loadUserData();
    window.addEventListener('userInfoChanged', handleUserInfoChange);
    
    return () => {
      window.removeEventListener('userInfoChanged', handleUserInfoChange);
    };
  }, [location.pathname]);

  if (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/reset-password') {
    return null;
  }

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleNotifMenuOpen = (event: React.MouseEvent<HTMLElement>) => setNotifAnchorEl(event.currentTarget);
  const handleNotifMenuClose = () => setNotifAnchorEl(null);

  const handleLogout = () => {
    // Check authentication before logout
    if (!isAuthenticated()) {

      alert('Authentication required. Please log in again.');
      window.location.href = '/login';
      return;
    }
    
    // Clear authentication and user-scoped caches
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_info');
    // Dashboard caches
    localStorage.removeItem('dashboard_cache');
    localStorage.removeItem('dashboard_cache_timestamp');
    // Any feature-specific caches can be cleared here as well
    sessionStorage.clear();
    navigate('/login');
  };

  return (
    <AppBar position="fixed" sx={{ background: '#0057b8', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, bgcolor: 'white', borderRadius: 1, p: 0.5 }}>
          <Link 
            to="/home" 
            style={{ display: 'flex', alignItems: 'center' }}
            onClick={(e) => {
              // Check authentication before navigation
              if (!isAuthenticated()) {

              e.preventDefault();
              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              window.location.href = '/login';
                return;
              }
            }}
          >
            <img src="/logo.png" alt="Logo" style={{ height: 40, marginRight: 0 }} />
          </Link>
        </Box>
        {(isAdminUser ? getAdminPrimaryLinks() : clientPrimaryLinks).map((link, index) => {
          const handleLinkClick = (e: React.MouseEvent) => {
            // Check authentication before navigation
            if (!isAuthenticated()) {
              e.preventDefault();
              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              window.location.href = '/login';
              return;
            }
            
            // For admin links with tabIndex, navigate to admin and set tab
            if (isAdminUser && (link as any).tabIndex !== undefined) {
              e.preventDefault();
              sessionStorage.setItem('adminTabIndex', (link as any).tabIndex.toString());
              window.location.href = '/admin';
            }
          };
          
          return (
            <Button
              key={link.to + index}
              color="inherit"
              component={isAdminUser && (link as any).tabIndex !== undefined ? 'button' : Link}
              to={isAdminUser && (link as any).tabIndex !== undefined ? undefined : link.to}
              sx={{ fontWeight: 600 }}
              onClick={handleLinkClick}
            >
              {link.label}
            </Button>
          );
        })}
        <Box sx={{ flexGrow: 1 }} />
        <IconButton 
          color="inherit" 
          onClick={(e) => {
            // Check authentication before opening notifications
            if (!isAuthenticated()) {

            e.preventDefault();
            sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
            window.location.href = '/login';
              return;
            }
            handleNotifMenuOpen(e);
          }}
        >
          <NotificationsIcon />
        </IconButton>
        <Menu
          anchorEl={notifAnchorEl}
          open={Boolean(notifAnchorEl)}
          onClose={handleNotifMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem disabled>No new notifications</MenuItem>
        </Menu>
        <Box sx={{ flexGrow: 0, ml: 2 }}>
          <IconButton 
            onClick={(e) => {
              // Check authentication before opening profile menu
              if (!isAuthenticated()) {

              e.preventDefault();
              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              window.location.href = '/login';
                return;
              }
              handleProfileMenuOpen(e);
            }} 
            sx={{ p: 0 }}
          >
            <Avatar alt={user.name} src={user.avatar}>
              {user.name ? getInitials(user.name) : <Person />}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { 
              // Check authentication before navigation
              if (!isAuthenticated()) {

              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              window.location.href = '/login';
                return;
              }
              handleMenuClose(); 
              navigate('/profile'); 
            }}>
              <Person fontSize="small" sx={{ mr: 1 }} /> Profile
            </MenuItem>
            <MenuItem onClick={() => { 
              // Check authentication before navigation
              if (!isAuthenticated()) {

              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              window.location.href = '/login';
                return;
              }
              handleMenuClose(); 
              navigate('/settings'); 
            }}>
              <Settings fontSize="small" sx={{ mr: 1 }} /> Settings
            </MenuItem>
            <MenuItem onClick={() => { 
              // Check authentication before navigation
              if (!isAuthenticated()) {

              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              window.location.href = '/login';
                return;
              }
              handleMenuClose(); 
              navigate('/help'); 
            }}>
              <AssignmentIcon fontSize="small" sx={{ mr: 1 }} /> Help
            </MenuItem>
            <MenuItem onClick={() => {
              // Check authentication before logout
              if (!isAuthenticated()) {

              sessionStorage.setItem('loginError', 'Your session expired. Please sign in again.');
              window.location.href = '/login';
                return;
              }
              handleLogout();
            }}>
              <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

const AppContent: React.FC = () => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/reset-password';
  const navigate = useNavigate();
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  // Soft validation only — never wipe refresh token on access-token expiry
  useEffect(() => {
    if (!isAuthPage) {
      validateAndCleanTokens();
    }
  }, [location.pathname, isAuthPage]);

  // Keep access tokens fresh so short Keycloak lifespans don't kick users out
  useEffect(() => {
    if (isAuthPage) return;

    let refreshTimer: number | undefined;
    let cancelled = false;
    let lastAttempt = 0;

    const schedule = () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      if (cancelled) return;

      const token = getToken();
      const refresh = localStorage.getItem('refresh_token');
      if (!token || !refresh) return;

      try {
        const decoded: any = jwtDecode(token);
        const expMs = decoded?.exp ? decoded.exp * 1000 : 0;
        if (!expMs) return;

        const bufferMs = 30_000; // refresh 30s before expiry
        const delay = Math.max(expMs - Date.now() - bufferMs, 0);

        refreshTimer = window.setTimeout(async () => {
          if (cancelled) return;
          const now = Date.now();
          if (now - lastAttempt < 5000) {
            schedule();
            return;
          }
          lastAttempt = now;

          const currentRefresh = localStorage.getItem('refresh_token');
          if (!currentRefresh) return;

          try {
            const data = await backendRefreshToken(currentRefresh);
            const newAccess = data.access_token || data.accessToken || data.token;
            const newRefresh = data.refresh_token || data.refreshToken;
            if (newAccess) localStorage.setItem('access_token', newAccess);
            if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
          } catch {
            // Leave session in place; ProtectedRoute / API interceptor will handle hard failures
          }
          schedule();
        }, delay);
      } catch {
        // Invalid access token — try an immediate refresh if possible
        if (!isAccessTokenValid() && refresh) {
          backendRefreshToken(refresh)
            .then((data: any) => {
              const newAccess = data.access_token || data.accessToken || data.token;
              const newRefresh = data.refresh_token || data.refreshToken;
              if (newAccess) localStorage.setItem('access_token', newAccess);
              if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
              schedule();
            })
            .catch(() => {});
        }
      }
    };

    schedule();

    return () => {
      cancelled = true;
      if (refreshTimer) window.clearTimeout(refreshTimer);
    };
  }, [isAuthPage, location.pathname]);

  // Global idle logout: 5 minutes of no activity clears session
  useEffect(() => {
    // Do not run idle logout while on auth pages (login/register/reset)
    if (isAuthPage) {
      return;
    }

    let idleTimer: number | undefined;
    const IDLE_MS = 5 * 60 * 1000; // 5 minutes

    const reset = () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        try {
          clearAuthData();
        } catch {}
        navigate('/login', { replace: true });
      }, IDLE_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [navigate, isAuthPage]);

  // Subscribe to API error stream for global snackbar
  useEffect(() => {
    const sub = apiError$.subscribe((msg: string) => {
      setSnackMsg(msg);
      setSnackOpen(true);
    });
    return () => sub.unsubscribe();
  }, []);

  return (
    <>
      <AppBarWithLinks />
      <Box sx={{ display: 'flex' }}>
        {!isAuthPage && <LeftSidebar />}
        <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/" element={<Navigate to="/home" replace />} />
            {/* Protected Routes */}
            <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
            <Route path="/loans" element={<ProtectedRoute><Loans /></ProtectedRoute>} />
            <Route path="/loan-application" element={<ProtectedRoute><LoanApplications /></ProtectedRoute>} />
            <Route path="/loan-update/:loanId" element={<ProtectedRoute><LoanUpdate /></ProtectedRoute>} />
            <Route path="/savings" element={<ProtectedRoute><Savings /></ProtectedRoute>} />
            <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
            <Route path="/statements" element={<ProtectedRoute><Statements /></ProtectedRoute>} />
            <Route path="/recent-transactions" element={<ProtectedRoute><RecentTransactions /></ProtectedRoute>} />
            <Route path="/charges" element={<ProtectedRoute><Charges /></ProtectedRoute>} />
            <Route path="/client-charges" element={<ProtectedRoute><ClientCharges /></ProtectedRoute>} />
            <Route path="/third-party-transfer" element={<ProtectedRoute><ThirdPartyTransfer /></ProtectedRoute>} />

            <Route path="/guarantor" element={<ProtectedRoute><Guarantor /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/qr" element={<ProtectedRoute><QR /></ProtectedRoute>} />
            <Route path="/location" element={<ProtectedRoute><Location /></ProtectedRoute>} />
            <Route path="/locations" element={<ProtectedRoute><Locations /></ProtectedRoute>} />
            <Route path="/survey" element={<ProtectedRoute><Survey /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/update-password" element={<ProtectedRoute><UpdatePassword /></ProtectedRoute>} />
            <Route path="/about-us" element={<ProtectedRoute><AboutUs /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
            <Route path="/share" element={<ProtectedRoute><Share /></ProtectedRoute>} />
            <Route path="/app-info" element={<ProtectedRoute><AppInfo /></ProtectedRoute>} />
            <Route path="/statement-pdf" element={<ProtectedRoute><StatementPdf /></ProtectedRoute>} />
            <Route path="/logout" element={<ProtectedRoute><LogoutPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/account-verification" element={<ProtectedRoute><AccountVerification /></ProtectedRoute>} />
            <Route path="/loan/:loanId" element={<ProtectedRoute><LoanDetails /></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
            <Route path="/payments/return" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />
            <Route path="/payments/result" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />
            {/* Payment route for loan application fees - must be protected */}
            <Route path="/payment" element={<ProtectedRoute><PaymentReturn /></ProtectedRoute>} />
            {/* Admin Dashboard - protected by AdminRoute */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            {/* Catch-all route - redirect to login for any undefined routes */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Box>
      </Box>
      {/* Global API error snackbar */}
      <GlobalSnackbar open={snackOpen} message={snackMsg} severity="error" onClose={() => setSnackOpen(false)} />
    </>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </Router>
  </AuthProvider>
);

export default App;



